import React, { createContext, useContext, useEffect, useState } from 'react';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { api } from '@/api/apiClient';

/**
 * Compatibility AuthContext.
 *
 * The original app had two stacked contexts: a Base44 auth layer and an
 * Odoo auth layer. We've removed Base44 entirely. This shim keeps the
 * `useAuth()` API alive so we don't have to rewrite every page that
 * imports it — it now just exposes the current Odoo user as `user`.
 *
 * Use `useOdooAuth()` for new code. Keep `useAuth()` for the legacy
 * surface: { user, isAuthenticated, isLoadingAuth, logout, ... }.
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { odooSession, isLoadingOdoo, logoutOdoo } = useOdooAuth();
  const [me, setMe] = useState(null);
  const [meChecked, setMeChecked] = useState(false);

  // After Odoo login, fetch /dashboard/api/me once to get canonical name +
  // email. The SellGo-app role comes from `odooSession.role` (populated by
  // OdooAuthContext from `/dashboard/api/user-role`), not from `/me`, so
  // there's a single source of truth for the role.
  useEffect(() => {
    let cancelled = false;
    async function loadMe() {
      if (!odooSession) {
        setMe(null);
        setMeChecked(true);
        return;
      }
      // The role is whatever OdooAuthContext put on the session.
      const role = odooSession.role === 'manager' ? 'manager' : 'rep';
      try {
        const res = await api.get('/me');
        if (!cancelled && res?.success) {
          setMe({
            id: res.data.id,
            uid: res.data.uid,
            full_name: res.data.name,
            email: res.data.email,
            login: res.data.login,
            role,
          });
        } else if (!cancelled) {
          // Fall back to session info
          setMe({
            id: odooSession.uid,
            uid: odooSession.uid,
            full_name: odooSession.name,
            email: odooSession.email,
            login: odooSession.login,
            role,
          });
        }
      } catch {
        if (!cancelled) {
          setMe({
            id: odooSession.uid,
            uid: odooSession.uid,
            full_name: odooSession.name,
            email: odooSession.email,
            login: odooSession.login,
            role,
          });
        }
      } finally {
        if (!cancelled) setMeChecked(true);
      }
    }
    loadMe();
    return () => {
      cancelled = true;
    };
  }, [odooSession]);

  const logout = () => logoutOdoo();
  const navigateToLogin = () => {
    /* no-op — App.jsx routes to <OdooLogin /> when there's no session */
  };

  return (
    <AuthContext.Provider
      value={{
        user: me,
        isAuthenticated: !!odooSession,
        isLoadingAuth: isLoadingOdoo || (odooSession && !meChecked),
        isLoadingPublicSettings: false,
        authError: null,
        appPublicSettings: null,
        authChecked: meChecked,
        logout,
        navigateToLogin,
        checkUserAuth: () => {},
        checkAppState: () => {},
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
